import { usePayments } from '@/data/hubspot/queries/receivables/payments';
import { InvoicesData } from '@/data/hubspot/types/invoices';
import { formatCurrency } from '@/lib/currency-utils';
import { Button, Popover, Table } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { drop, lowerCase, startCase, take } from 'lodash';
import { useState } from 'react';

export default function PaymentList2() {
  // Local States
  const [page, setPage] = useState(1);
  const [isDescending, setIsDescending] = useState(true);

  // Queries
  const { data, isLoading } = usePayments({
    page,
    sort_by: isDescending ? 'DESC' : 'ASC',
    sort_col: 'AMOUNT',
  });

  return (
    <div>
      <Table
        loading={isLoading}
        className="m-4"
        scroll={{ x: 'max-content' }}
        onChange={(_pagination, _filters, sorter) => {
          const newIsDescending = (sorter as SorterResult<InvoicesData>).order === 'descend';
          setIsDescending(newIsDescending);
          const isSorted = newIsDescending !== isDescending;
          if (isSorted) {
            setPage(1);
          }
        }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: false,
          current: page,
          total: data?.total_records,
          onChange(page) {
            setPage(page);
          },
        }}
        columns={[
          {
            title: 'Reference#',
            key: 'reference',
            render: (_, record) => (
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[var(--brand-primary)]">{record.reference_no}</span>
                </div>
              </div>
            ),
          },

          {
            title: 'Payment Date',
            key: 'paymentDate',
            render: (_, record) => <div>{dayjs(record.payment_date).format('ll')}</div>,
          },
          {
            title: 'Payment Amount',
            key: 'amount',
            sorter: true,
            sortOrder: isDescending ? 'descend' : 'ascend',
            sortDirections: ['descend', 'ascend', 'descend'],
            render: (_, record) => (
              <div className="flex flex-col">
                {formatCurrency(record.amount, {
                  currency: record.currency,
                })}
              </div>
            ),
          },
          {
            title: 'Invoices',
            key: 'invoices',
            render: (_, { invoices }) => {
              const first2 = take(invoices ?? [], 1);
              const rest = drop(invoices ?? [], 1);
              return (
                <div className="flex flex-col">
                  {first2.map((x) => (
                    <div key={x.id}>{x.invoice_no}</div>
                  ))}
                  {!!rest.length && (
                    <Popover
                      content={
                        <div>
                          {rest.map((x) => (
                            <div key={x.id}>{x.invoice_no}</div>
                          ))}
                        </div>
                      }
                    >
                      <span className='text-[var(--brand-primary-light)]'>+{rest.length}</span>
                    </Popover>
                  )}
                </div>
              );
            },
          },
          {
            title: 'Payment Mode',
            key: 'paymentMode',
            render: (_, record) => (
              <div className="flex flex-col">
                <div>{record.mode}</div>
              </div>
            ),
          },
          {
            title: 'Description',
            key: 'notes',
            render: (_, record) => <div>{record.notes}</div>,
          },

          {
            title: 'Status',
            key: 'status',
            render: (_, record) => (
              <div className="flex flex-col">
                <span> {startCase(lowerCase(record.status))}</span>
              </div>
            ),
          },
        ]}
        dataSource={data?.list}
      />
    </div>
  );
}
